import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Container, Item } from 'Components/Grid';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { HistoryEventListItem } from 'Components/_NewElements/HistoryEvents';
import { useState } from 'react';
import { useMyServiceContext } from '../..';
import { actions } from 'Store/myapp/actions';
import { useAppDispatch } from 'Store/index';
import ToolTip from 'Components/ToolTip';
import { getRecurringTaskInstances } from 'neuro-common-utils';
import { DateTime } from 'luxon';
import { convertTaskRecurrenceToTTaskRecurrence, TASK_DATE_FORMAT } from 'Utility/tasklist';

const formatTaskDate = ({
  date,
  timezone,
  format = TASK_DATE_FORMAT,
  exclusive,
}: {
  date: string;
  timezone: string;
  format?: typeof TASK_DATE_FORMAT | 'ISO';
  exclusive: boolean;
}): string => {
  return DateTime.fromFormat(date, format)
    .setZone(timezone)
    .minus({ milliseconds: exclusive ? 1 : 0 })
    .toFormat('yyyy-MM-dd');
};

const RecurringTaskSingle = ({ task, platform, showEndButton, index }: IRecurringTaskSingle): React.ReactNode => {
  const { fm, setEditingObj } = useMyServiceContext();

  const [openRecurring, setOpenRecurring] = useState<boolean>(index === 0);

  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const handleEndClick = async (taskList: ITaskList) => {
    setLoading(true);
    await actions.updateTasklist(
      { ...taskList, end_exclusive: DateTime.now().setZone(task.timezone).toFormat('yyyy-MM-dd HH:mm:ss') },
      dispatch,
    );
    setLoading(false);
  };

  const tasks = task.tasks.map(t => fm(`myService.${platform}.opts.${t}`));
  // if (!tasks) return null;
  const recurrence = convertTaskRecurrenceToTTaskRecurrence({ recurrence: task.recurrence });
  if (!recurrence) return null;

  const instances = getRecurringTaskInstances({
    task: {
      recurrence,
      timezone: task.timezone,
      start_inclusive: task.start_inclusive,
      end_exclusive: task.end_exclusive,
    },
  });

  return (
    <>
      <BlockWrapper
        key={task.id}
        title={tasks.join(', ')}
        contentMargin={1}
        collapse={{
          open: openRecurring,
          collapseHandler: () => setOpenRecurring(prev => !prev),
        }}
      >
        <Container>
          <Item xs={true}>
            <HistoryEventListItem
              titleWidth={5}
              title={'myService.recurrence'}
              value={
                !task.end_exclusive
                  ? fm('myService.every_nth', { value: task.recurrence.every_nth })
                  : `${fm('myService.every_nth', { value: task.recurrence.every_nth })}, ${fm('myService.until', {
                      until: formatTaskDate({ date: task.end_exclusive, timezone: task.timezone, exclusive: true }),
                    })}`
              }
            />
            <HistoryEventListItem
              titleWidth={5}
              title={'myService.latestDeadline'}
              value={
                instances?.previous
                  ? formatTaskDate({
                      date: instances.previous.endExclusiveIso,
                      timezone: task.timezone,
                      exclusive: true,
                      format: 'ISO',
                    })
                  : undefined
              }
            />
            <HistoryEventListItem
              titleWidth={5}
              title={'myService.latestSendingDate'}
              value={
                instances?.previous
                  ? formatTaskDate({
                      date: instances.previous.startInclusiveIso,
                      timezone: task.timezone,
                      exclusive: false,
                      format: 'ISO',
                    })
                  : undefined
              }
            />
            <HistoryEventListItem
              titleWidth={5}
              title={'myService.nextDeadline'}
              value={
                instances?.current || instances?.next
                  ? formatTaskDate({
                      date: (instances.current?.endExclusiveIso ?? instances.next?.endExclusiveIso) as string,
                      timezone: task.timezone,
                      exclusive: true,
                      format: 'ISO',
                    })
                  : undefined
              }
            />
            <HistoryEventListItem
              titleWidth={5}
              title={'myService.nextSendingDate'}
              value={
                instances?.current || instances?.next
                  ? formatTaskDate({
                      date: (instances.current?.startInclusiveIso ?? instances.next?.startInclusiveIso) as string,
                      timezone: task.timezone,
                      exclusive: false,
                      format: 'ISO',
                    })
                  : undefined
              }
            />
          </Item>
          {showEndButton ? (
            <Item xs={3.5} md={3} style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ marginTop: '1rem' }}>
                <ActionButtonRounded
                  text="general.edit"
                  filled={true}
                  uppercase={false}
                  width={9.6}
                  height={3}
                  fontSize={16}
                  onClick={() => setEditingObj({ type: '_tasklist', data: task })}
                />
                <div style={{ marginTop: '1rem' }} />
                <ToolTip
                  title={fm('myService.endRecurringDisabledTooltip')}
                  description={fm('myService.endRecurringDisabledTooltipDescription')}
                  hover
                  content={
                    <ActionButtonRounded
                      text="myService.endRecurring"
                      filled={false}
                      uppercase={false}
                      width={9.6}
                      height={3}
                      fontSize={16}
                      onClick={() => handleEndClick(task)}
                      // disabled if start in future
                      disabled={DateTime.fromFormat(task.start_inclusive, TASK_DATE_FORMAT) > DateTime.now()}
                      colorScheme="error"
                      loading={loading}
                    />
                  }
                />
              </div>
            </Item>
          ) : null}
        </Container>
      </BlockWrapper>
      <div style={{ marginBottom: '2rem' }} />
    </>
  );
};

interface IRecurringTaskSingle {
  task: ITaskList;
  platform: Platform;
  showEndButton?: boolean;
  index: number;
}

const RecurringTasksList = ({ platform, recurringTasks, showEndButton }: IOwnProps) => {
  const { fm } = useMyServiceContext();

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div>
        <HistoryRowSubHeader header={fm('myService.recurringTasks')} fontSize="small" />
      </div>
      <div style={{ padding: 1 }}>
        {recurringTasks.map((rt, i) => (
          <RecurringTaskSingle key={rt.id} task={rt} platform={platform} showEndButton={showEndButton} index={i} />
        ))}
      </div>
    </div>
  );
};

interface IOwnProps {
  platform: Platform;
  recurringTasks: ITaskList[];
  showEndButton?: boolean;
}

export default RecurringTasksList;
